/* eslint-disable @typescript-eslint/no-explicit-any */
// components/SearchBox.tsx
import React, { useState, useRef, useEffect } from "react";
import {
  Search,
  X,
  Clock,
  Building,
  Calendar,
  FileText,
  Briefcase,
  Loader,
  MapPin,
  Tag,
  AlertCircle,
} from "lucide-react";
import { useSearch } from "../context/SearchContext";
import { useDarkMode } from "../context/DarkModeContext";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

// Mock data for fallback when backend is down
const mockSearchResults = [
  {
    id: "1",
    type: "job" as const,
    title: "Frontend Developer - React",
    description:
      "Looking for experienced React developer with TypeScript knowledge",
    url: "/jobs/1",
    relevance: 0.9,
    tags: ["React", "TypeScript", "Frontend", "Remote"],
  },
  {
    id: "2",
    type: "job" as const,
    title: "Backend Engineer - Node.js",
    description: "Senior backend developer with Node.js and MongoDB experience",
    url: "/jobs/2",
    relevance: 0.8,
    tags: ["Node.js", "MongoDB", "Backend", "API"],
  },
  {
    id: "3",
    type: "event" as const,
    title: "Tech Career Fair 2024",
    description: "Annual tech career fair with top companies hiring",
    url: "/events/1",
    relevance: 0.7,
    tags: ["Career", "Networking", "Hiring"],
  },
  {
    id: "4",
    type: "article" as const,
    title: "How to Prepare for Technical Interviews",
    description: "Comprehensive guide to acing your next technical interview",
    url: "/articles/1",
    relevance: 0.6,
    tags: ["Interview", "Career", "Tips"],
  },
];

const mockSuggestions = [
  { type: "job" as const, text: "React Developer", category: "Technology" },
  { type: "job" as const, text: "Remote Jobs", category: "Work Type" },
  { type: "location" as const, text: "Nairobi", category: "Location" },
  { type: "location" as const, text: "Mombasa", category: "Location" },
  {
    type: "category" as const,
    text: "Software Engineering",
    category: "Field",
  },
];

const SearchBox: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [localQuery, setLocalQuery] = useState("");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);

  const {
    searchFilters,
    searchResults,
    isSearching,
    showSearchResults,
    performSearch,
    updateFilters,
    setShowSearchResults,
    recentSearches,
    quickSearch,
    clearRecentSearches,
    getSearchSuggestions,
    searchError,
  } = useSearch();

  const { isDark } = useDarkMode();
  const { user } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);

  // Sync local query with global filters
  useEffect(() => {
    setLocalQuery(searchFilters.query);
  }, [searchFilters.query]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchBoxRef.current &&
        !searchBoxRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setShowSearchResults(false);
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setShowSearchResults]);

  // Load suggestions when query changes - WITH FALLBACK
  useEffect(() => {
    const loadSuggestions = async () => {
      if (localQuery.length >= 2) {
        try {
          const suggestionResults = await getSearchSuggestions(localQuery);
          if (suggestionResults.length > 0) {
            setSuggestions(suggestionResults);
            setUsingFallback(false);
          } else {
            // Use mock suggestions if API returns empty
            const filteredMock = mockSuggestions.filter((suggestion) =>
              suggestion.text.toLowerCase().includes(localQuery.toLowerCase()),
            );
            setSuggestions(filteredMock);
            setUsingFallback(true);
          }
          setShowSuggestions(true);
        } catch (error) {
          console.error("Failed to load suggestions, using fallback:", error);
          // Use filtered mock suggestions as fallback
          const filteredMock = mockSuggestions.filter((suggestion) =>
            suggestion.text.toLowerCase().includes(localQuery.toLowerCase()),
          );
          setSuggestions(filteredMock);
          setShowSuggestions(true);
          setUsingFallback(true);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
        setUsingFallback(false);
      }
    };

    const timeoutId = setTimeout(loadSuggestions, 300); // Debounce
    return () => clearTimeout(timeoutId);
  }, [localQuery, getSearchSuggestions]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalQuery(value);
    updateFilters({ query: value });

    if (value.length > 2) {
      performSearch(value);
    } else {
      setShowSearchResults(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (localQuery.trim()) {
      performSearch();
      setIsOpen(false);
      setShowSuggestions(false);
      navigate("/find-job", { state: { searchQuery: localQuery } });
    }
  };

  const handleQuickSearch = (query: string, type?: any) => {
    quickSearch(query, type);
    setIsOpen(false);
    setShowSuggestions(false);
    navigate("/find-job", { state: { searchQuery: query } });
  };

  const handleSuggestionClick = (suggestion: any) => {
    setLocalQuery(suggestion.text);
    updateFilters({ query: suggestion.text });
    performSearch(suggestion.text);
    setShowSuggestions(false);
  };

  const handleResultClick = (result: any) => {
    navigate(result.url);
    setIsOpen(false);
    setShowSearchResults(false);
    setShowSuggestions(false);
  };

  const clearSearch = () => {
    setLocalQuery("");
    updateFilters({ query: "" });
    setShowSearchResults(false);
    setShowSuggestions(false);
    setSuggestions([]);
    setUsingFallback(false);
  };

  const getIconForType = (type: string) => {
    switch (type) {
      case "job":
        return <Briefcase size={16} />;
      case "event":
        return <Calendar size={16} />;
      case "article":
        return <FileText size={16} />;
      case "company":
        return <Building size={16} />;
      case "location":
        return <MapPin size={16} />;
      case "category":
        return <Tag size={16} />;
      default:
        return <Search size={16} />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "job":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "event":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
      case "article":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
      case "company":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200";
      case "location":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      case "category":
        return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
    }
  };

  const getSuggestionIcon = (type: string) => {
    switch (type) {
      case "job":
        return <Briefcase size={14} className="text-green-500" />;
      case "company":
        return <Building size={14} className="text-orange-500" />;
      case "location":
        return <MapPin size={14} className="text-red-500" />;
      case "category":
        return <Tag size={14} className="text-indigo-500" />;
      default:
        return <Search size={14} className="text-gray-500" />;
    }
  };

  // Quick search categories based on user preferences
  const quickSearchCategories = [
    {
      query: "React Developer",
      type: "jobs",
      icon: Briefcase,
      color: "text-green-500",
    },
    {
      query: "Remote Jobs",
      type: "jobs",
      icon: Briefcase,
      color: "text-green-500",
    },
    {
      query: "Tech Events",
      type: "events",
      icon: Calendar,
      color: "text-blue-500",
    },
    {
      query: "Career Advice",
      type: "articles",
      icon: FileText,
      color: "text-purple-500",
    },
    { query: "Nairobi", type: "jobs", icon: MapPin, color: "text-red-500" },
    { query: "Mombasa", type: "jobs", icon: MapPin, color: "text-red-500" },
  ];

  // Filter mock results based on current query for fallback
  const getFilteredMockResults = () => {
    if (!localQuery.trim()) return mockSearchResults;

    return mockSearchResults.filter(
      (result) =>
        result.title.toLowerCase().includes(localQuery.toLowerCase()) ||
        result.description.toLowerCase().includes(localQuery.toLowerCase()) ||
        result.tags?.some((tag: string) =>
          tag.toLowerCase().includes(localQuery.toLowerCase()),
        ),
    );
  };

  const displayResults =
    searchError || usingFallback ? getFilteredMockResults() : searchResults;
  const hasResults = displayResults.length > 0;

  return (
    <div ref={searchBoxRef} className="relative w-full max-w-2xl">
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative">
          <Search
            className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
              isDark ? "text-gray-400" : "text-gray-500"
            }`}
            size={20}
          />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search jobs, events, articles, companies..."
            value={localQuery}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            className={`w-full pl-10 pr-10 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-sky-500 transition-all duration-300 ${
              isDark
                ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
            }`}
          />
          {localQuery && (
            <button
              type="button"
              onClick={clearSearch}
              className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded transition-colors ${
                isDark
                  ? "text-gray-400 hover:text-gray-300 hover:bg-gray-700"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-200"
              }`}
            >
              <X size={16} />
            </button>
          )}
        </div>
      </form>

      {/* Error Message with Fallback Indicator */}
      {(searchError || usingFallback) && isOpen && (
        <div
          className={`absolute top-full left-0 right-0 mt-2 p-3 rounded-lg text-sm ${
            isDark
              ? "bg-yellow-900/20 border border-yellow-700 text-yellow-200"
              : "bg-yellow-50 border border-yellow-200 text-yellow-700"
          }`}
        >
          <div className="flex items-center space-x-2">
            <AlertCircle size={16} />
            <div className="flex-1">
              <div className="font-medium">
                {usingFallback
                  ? "Using demo data"
                  : "Search service temporarily unavailable"}
              </div>
              <div className="text-xs mt-1">
                {usingFallback
                  ? "Showing sample results while backend is being restored"
                  : "Please try again in a few moments"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Suggestions */}
      {isOpen && showSuggestions && suggestions.length > 0 && (
        <div
          className={`absolute top-full left-0 right-0 mt-2 rounded-lg shadow-xl border z-50 max-h-80 overflow-y-auto ${
            isDark ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200"
          }`}
        >
          <div className="p-2">
            <div className="flex justify-between items-center px-3 py-2">
              <div
                className={`text-xs font-semibold ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                SUGGESTIONS
              </div>
              {usingFallback && (
                <div
                  className={`text-xs px-2 py-1 rounded ${
                    isDark
                      ? "bg-yellow-900 text-yellow-200"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  Demo
                </div>
              )}
            </div>
            {suggestions.map((suggestion, index) => (
              <button
                key={`${suggestion.type}-${index}`}
                onClick={() => handleSuggestionClick(suggestion)}
                className={`w-full text-left p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center space-x-3 ${
                  isDark ? "text-gray-200" : "text-gray-900"
                }`}
              >
                {getSuggestionIcon(suggestion.type)}
                <div className="flex-1">
                  <div className="text-sm font-medium">{suggestion.text}</div>
                  <div
                    className={`text-xs ${
                      isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {suggestion.category}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search Results & Recent Searches */}
      {isOpen &&
        (showSearchResults || recentSearches.length > 0 || hasResults) &&
        !showSuggestions && (
          <div
            className={`absolute top-full left-0 right-0 mt-2 rounded-lg shadow-xl border z-50 max-h-96 overflow-y-auto ${
              isDark
                ? "bg-gray-800 border-gray-600"
                : "bg-white border-gray-200"
            }`}
          >
            {/* Search Results */}
            {(showSearchResults || hasResults) && (
              <div className="p-2">
                {isSearching ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader size={20} className="animate-spin text-sky-500" />
                    <span
                      className={`ml-2 ${isDark ? "text-gray-300" : "text-gray-600"}`}
                    >
                      Searching...
                    </span>
                  </div>
                ) : hasResults ? (
                  <div>
                    <div className="flex justify-between items-center px-3 py-2">
                      <div
                        className={`text-xs font-semibold ${
                          isDark ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        SEARCH RESULTS ({displayResults.length})
                        {usingFallback && (
                          <span
                            className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                              isDark
                                ? "bg-yellow-900 text-yellow-200"
                                : "bg-yellow-100 text-yellow-700"
                            }`}
                          >
                            Demo
                          </span>
                        )}
                      </div>
                    </div>
                    {displayResults.slice(0, 8).map((result) => (
                      <button
                        key={`${result.type}-${result.id}`}
                        onClick={() => handleResultClick(result)}
                        className={`w-full text-left p-3 rounded-lg hover:bg-sky-50 dark:hover:bg-sky-900/20 transition-colors duration-200 mb-1 ${
                          isDark ? "text-gray-200" : "text-gray-900"
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div
                            className={`p-2 rounded-full ${getTypeColor(result.type)}`}
                          >
                            {getIconForType(result.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <span
                                className={`text-xs px-2 py-1 rounded-full ${getTypeColor(result.type)}`}
                              >
                                {result.type.toUpperCase()}
                              </span>
                              <span className="text-sm font-medium truncate">
                                {result.title}
                              </span>
                            </div>
                            <p
                              className={`text-sm truncate ${
                                isDark ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {result.description}
                            </p>
                            {result.tags && result.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {result.tags.slice(0, 3).map((tag) => (
                                  <span
                                    key={tag}
                                    className={`text-xs px-2 py-1 rounded ${
                                      isDark
                                        ? "bg-gray-700 text-gray-300"
                                        : "bg-gray-200 text-gray-700"
                                    }`}
                                  >
                                    #{tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                    {displayResults.length > 8 && (
                      <button
                        onClick={() => {
                          setIsOpen(false);
                          navigate("/find-job", {
                            state: { searchQuery: localQuery },
                          });
                        }}
                        className={`w-full text-center p-3 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors ${
                          isDark ? "text-sky-400" : "text-sky-600"
                        }`}
                      >
                        View all {displayResults.length} results
                      </button>
                    )}
                  </div>
                ) : localQuery.length > 2 ? (
                  <div
                    className={`text-center py-4 ${
                      isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    <div className="text-lg mb-2">🔍</div>
                    <div>No results found for "{localQuery}"</div>
                    <div className="text-sm mt-1">
                      Try different keywords or filters
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {/* Recent Searches */}
            {!showSearchResults && recentSearches.length > 0 && (
              <div className="p-2">
                <div className="flex justify-between items-center px-3 py-2">
                  <div
                    className={`text-xs font-semibold ${
                      isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    RECENT SEARCHES
                  </div>
                  <button
                    onClick={clearRecentSearches}
                    className={`text-xs hover:underline ${
                      isDark
                        ? "text-gray-400 hover:text-gray-300"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Clear all
                  </button>
                </div>
                {recentSearches.map((search, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickSearch(search)}
                    className={`w-full text-left p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center space-x-3 ${
                      isDark ? "text-gray-200" : "text-gray-900"
                    }`}
                  >
                    <Clock
                      size={16}
                      className={isDark ? "text-gray-400" : "text-gray-500"}
                    />
                    <span>{search}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Quick Search Suggestions */}
            <div className="border-t border-gray-200 dark:border-gray-600 p-2">
              <div
                className={`text-xs font-semibold px-3 py-2 ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                QUICK SEARCH
              </div>
              <div className="grid grid-cols-2 gap-2">
                {quickSearchCategories.map((item, index) => {
                  const IconComponent = item.icon;
                  return (
                    <button
                      key={index}
                      onClick={() =>
                        handleQuickSearch(item.query, item.type as any)
                      }
                      className={`p-2 rounded-lg text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 ${
                        isDark ? "text-gray-200" : "text-gray-900"
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <IconComponent size={14} className={item.color} />
                        <span className="text-sm">{item.query}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Search Tips */}
            {user && (
              <div className="border-t border-gray-200 dark:border-gray-600 p-3">
                <div
                  className={`text-xs font-semibold mb-2 ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  SEARCH TIPS
                </div>
                <div className="text-xs space-y-1">
                  <div className={isDark ? "text-gray-400" : "text-gray-500"}>
                    • Use quotes for exact matches: "senior developer"
                  </div>
                  <div className={isDark ? "text-gray-400" : "text-gray-500"}>
                    • Use location filters for local jobs
                  </div>
                  <div className={isDark ? "text-gray-400" : "text-gray-500"}>
                    • Try different job types (remote, full-time, etc.)
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
    </div>
  );
};

export default SearchBox;
